/**
 * Pre-launch widget chrome — the parts that live outside the chat
 * panel: the pill launcher (ICON layout), the welcome speech bubble
 * (hint above the launcher), and the FULL-layout backdrop.
 *
 * First chunk of Group A in the Preact rewrite (`widget/preact-rewrite`).
 * Each component is mounted into its own anchor by `mountWidget` in
 * `ui.tsx`; signals drive the mutable fields (agent name, avatar URL,
 * welcome message text + visibility). The rest of `ui.tsx` continues
 * to own the layout state-machine + DOM lifecycle.
 */

import type { Signal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { type Layout, LAYOUTS } from '../layouts';

export interface AgentChromeBits {
  /** First-name label on the launcher pill — "Ask Alex". */
  firstName: Signal<string>;
  /** Avatar URL for launcher + welcome bubble. */
  avatarUrl: Signal<string>;
}

export interface NamedLauncherExtras {
  /** Phase 3 (behind ui.mobileV2): the launcher circle plays the greeter clip. */
  mobileV2?: Signal<boolean>;
  /** Greeter clip configured (modes B–D) — gates the dock vs the still. */
  hasGreeter?: Signal<boolean>;
  /** Voice available (mode ≥C) — shows the citron live dot. */
  liveDot?: Signal<boolean>;
  /** Register/release the avatar-stage 'launcher' dock. */
  dock?: { register(anchor: HTMLElement): void; release(): void };
  /** Battery hygiene — set true when offscreen / idle to pause the loop. */
  onIdlePause?: (paused: boolean) => void;
  /** Phase 5 (O1) — collapsed-offer count badge. 0 = hidden. */
  badge?: Signal<number>;
}

export interface WelcomeBubbleBits extends AgentChromeBits {
  /** Speech text — server-provided agent.welcomeMessage. */
  text: Signal<string>;
  /** Visibility — shown on ICON layout when a welcome message exists, hidden on launcher click. */
  visible: Signal<boolean>;
}

export function Launcher({
  bits,
  extras,
  onClick,
}: {
  bits: AgentChromeBits;
  extras?: NamedLauncherExtras;
  onClick: () => void;
}) {
  const mv2 = extras?.mobileV2?.value ?? false;
  const useDock = mv2 && (extras?.hasGreeter?.value ?? false) && !!extras?.dock;
  const liveDot = extras?.liveDot?.value ?? false;
  const dockRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLButtonElement>(null);


  // Dock the persistent greeter clip into the launcher circle (§5.1:
  // the avatar IS the greeter, same <video>, never a separate load).
  useEffect(() => {
    if (!useDock) return;
    const anchor = dockRef.current;
    if (!anchor) return;
    extras!.dock!.register(anchor);
    return () => extras!.dock!.release();
  }, [useDock]);

  // Battery hygiene (§5.1): pause the loop when the launcher is
  // offscreen and after ~30s idle; resume on visibility/interaction.
  useEffect(() => {
    if (!useDock || !extras?.onIdlePause) return;
    const node = rootRef.current;
    if (!node) return;
    const pause = extras.onIdlePause;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const armIdle = (): void => {
      if (idleTimer) clearTimeout(idleTimer);
      pause(false);
      idleTimer = setTimeout(() => pause(true), 30_000);
    };
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver((entries) => {
        const visible = entries[0]?.isIntersecting ?? true;
        if (!visible) pause(true);
        else armIdle();
      });
      io.observe(node);
    }
    const onActivity = (): void => armIdle();
    window.addEventListener('scroll', onActivity, { passive: true });
    window.addEventListener('pointerdown', onActivity, { passive: true });
    armIdle();
    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      io?.disconnect();
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('pointerdown', onActivity);
      pause(false);
    };
  }, [useDock]);

  return (
    <button
      ref={rootRef}
      type="button"
      class={`sp-launcher sp-launcher--enter${mv2 ? ' sp-launcher--mv2' : ''}`}
      aria-label="Open chat"
      onClick={onClick}
      onAnimationEnd={(e) => {
        // Entrance beat runs ONCE (§5.1) — strip the class so later
        // re-renders / page interactions never replay it.
        if ((e as AnimationEvent).animationName === 'sp-launcher-in') {
          rootRef.current?.classList.remove('sp-launcher--enter');
        }
      }}
    >
      <div class="sp-launcher-avatar">
        {useDock ? (
          /* The avatar-stage launcher dock — the persistent greeter <video>. */
          <div ref={dockRef} class="sp-launcher-dock" />
        ) : (
          <img src={bits.avatarUrl.value} alt="" />
        )}
        {liveDot ? <span class="sp-launcher-live-dot" aria-hidden="true" /> : null}
        {(extras?.badge?.value ?? 0) > 0 ? (
          <span class="sp-launcher-badge">{extras!.badge!.value}</span>
        ) : null}
      </div>
      <span class="sp-launcher-label">
        {bits.firstName.value ? `Ask ${bits.firstName.value}` : 'Ask'}
      </span>
    </button>
  );
}

export function WelcomeBubble({ bits }: { bits: WelcomeBubbleBits }) {
  // Display:none when hidden so the bubble doesn't take layout space
  // (legacy parity — toggled inline rather than via class so the same
  // selector works pre-/post-interaction).
  const style = bits.visible.value ? '' : 'display:none';
  return (
    <div class="sp-welcome-bubble" style={style}>
      <div class="sp-welcome-avatar">
        <img src={bits.avatarUrl.value} alt="" />
      </div>
      <div class="sp-welcome-text">{bits.text.value}</div>
    </div>
  );
}

export function Backdrop({ onClick }: { onClick: () => void }) {
  return <div class="sp-full-backdrop" onClick={onClick} />;
}

/**
 * "Speak with {name}" CTA — the chat-mode entry into a voice/video session.
 * A pill at the top of the chat thread; hidden during a voice session (the
 * card becomes the presence). Only renders when voice is wired/available.
 */
export function SpeakCta({
  agentName,
  avatarUrl,
  voiceAvailable,
  onSpeak,
}: {
  agentName: Signal<string>;
  avatarUrl: Signal<string>;
  voiceAvailable: Signal<boolean>;
  onSpeak: () => void;
}) {
  if (!voiceAvailable.value) return null;
  const n = agentName.value;
  const name = n && n !== 'Chat' ? n : 'the agent';
  return (
    <button type="button" class="sp-speak-cta" onClick={() => onSpeak()}>
      <img class="sp-speak-cta-avatar" src={avatarUrl.value} alt="" />
      <svg
        class="sp-speak-cta-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
      </svg>
      <span>Speak with {name}</span>
    </button>
  );
}

export interface HeaderBits {
  agentName: Signal<string>;
  /** Agent role/title shown lighter beside the name (e.g. "AI Agent"). */
  agentTitle?: Signal<string>;
  avatarUrl: Signal<string>;
  currentLayout: Signal<Layout>;
  /**
   * The agent's product view — drives which header buttons render.
   * Per agent-flows.md: Chat Button + Rail are "one tier above Dock"
   * products, so they only need a close (X). Site Agent has prompt /
   * conversation / rail tiers so it shows minimize + sidebar +
   * fullscreen + close. Engage shows minimize + close (compact ↔ open).
   */
  view: Signal<'site-agent' | 'chat-button' | 'engage' | 'rail' | 'unbound'>;
  /** Content exists this session — gates the fullscreen (content
   * experience) button so it never opens an empty shell. */
  hasContent: Signal<boolean>;
  /** Mobile/tablet viewport — drives sidebar button visibility. */
  isTablet: () => boolean;
  /**
   * Standalone Experience (P2) — the widget IS the whole page (share
   * link / experience embed). Minimize + close would collapse to the
   * chat launcher over a blank page, which is meaningless here, so both
   * are hidden. Undefined/false for a normal embed on a host page.
   */
  standaloneExperience?: Signal<boolean>;
}

export interface HeaderHandlers {
  onMinimize: () => void;
  onSidebar: () => void;
  onFullscreen: () => void;
  onClose: () => void;
}

function HeaderIcon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

/**
 * Chat-panel header: agent avatar + name, voice status badge anchor,
 * minimize / sidebar / fullscreen / close buttons. The voice anchor is
 * a plain `<div class="sp-voice-badge-anchor">` slot — the voice
 * surface mounts its Preact island into it via a query in mountWidget.
 */
export function Header({
  bits,
  handlers,
}: {
  bits: HeaderBits;
  handlers: HeaderHandlers;
}) {
  const layout = bits.currentLayout.value;
  const view = bits.view.value;
  // Per-view button visibility — agent-flows.md says Chat Button +
  // Rail are single-tier (minimize and close resolve to the same
  // destination: dock), so they render close only. Site Agent has
  // multiple tiers; Engage has compact ↔ open.
  // chat-button + rail are single-tier so they normally hide the
  // minimize button. Exception: on FULL (content viewer), every view
  // gets minimize + sidebar + close so the visitor can dismiss content
  // without losing the chat panel.
  // Standalone experience = the widget is the whole page; there's nothing
  // to minimize/close TO (collapsing lands a chat button over blank). Hide
  // both dismiss controls.
  const standalone = bits.standaloneExperience?.value ?? false;
  const showMinimize =
    !standalone &&
    (layout === LAYOUTS.FULL || (view !== 'chat-button' && view !== 'rail'));
  const showSidebar =
    !standalone && // standalone experience can't peel to rail — no way back
    !bits.isTablet() &&
    (layout === LAYOUTS.FULL
      ? view !== 'rail' // rail is already SIDEBAR underneath — no second nav needed
      : view === 'site-agent' && layout !== LAYOUTS.SIDEBAR);
  const showFullscreen =
    view === 'site-agent' && layout !== LAYOUTS.FULL && bits.hasContent.value;
  return (
    <div class="sp-header">
      <div class="sp-header-avatar-row">
        <div class="sp-header-avatar">
          <img src={bits.avatarUrl.value} alt="" />
        </div>
        <div class="sp-header-id">
          <span class="sp-header-name">{bits.agentName.value || 'Chat'}</span>
          {bits.agentTitle?.value ? (
            <span class="sp-header-role">{bits.agentTitle.value}</span>
          ) : null}
        </div>
      </div>
      <div class="sp-voice-badge-anchor" />
      <div class="sp-header-actions">
        {showMinimize ? (
          <button
            type="button"
            class="sp-header-btn"
            aria-label="Minimize"
            onClick={handlers.onMinimize}
          >
            <HeaderIcon d='<path d="M5 12H19"/>' />
          </button>
        ) : null}
        {showSidebar ? (
          <button
            type="button"
            class="sp-header-btn"
            aria-label="Align to sidebar"
            onClick={handlers.onSidebar}
          >
            <HeaderIcon d='<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M15 3v18"/>' />
          </button>
        ) : null}
        {showFullscreen ? (
          <button
            type="button"
            class="sp-header-btn"
            aria-label="Fullscreen"
            onClick={handlers.onFullscreen}
          >
            <HeaderIcon d='<path d="M15 3h6v6"/><path d="m21 3-7 7"/><path d="m3 21 7-7"/><path d="M9 21H3v-6"/>' />
          </button>
        ) : null}
        {standalone ? null : (
          <button
            type="button"
            class="sp-header-btn sp-close-btn"
            aria-label="Close chat"
            onClick={handlers.onClose}
          >
            <HeaderIcon d='<path d="M18 6L6 18M6 6L18 18"/>' />
          </button>
        )}
      </div>
    </div>
  );
}
